import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { ChevronDownIcon, LogOutIcon, MenuIcon, XIcon } from 'lucide-react';

import { useBreakpoint } from '@mds/shared/hooks/use-media-query';
import { cn } from '@mds/shared/lib/utils';

import { confirmLeave } from '@/hooks/use-unsaved';
import { useSession } from '@/auth/use-session';
import { displayName, initials } from '@/lib/initials';
import UserMenu from '@/components/layout/user-menu';

const ICONS: Record<string, string> = {
  '/': 'M2.6 7.2L8 2.8l5.4 4.4v6H2.6z M6.4 13.2V9.4h3.2v3.8',
  '/bookings': 'M3 5.5h10v8H3z M3 8.5h10 M5.8 3.4v3 M10.2 3.4v3',
  '/bookings/feedback':
    'M2.6 3h10.8v7.4H7.2L4.4 13v-2.6H2.6z M8 5l.7 1.4 1.5.2-1.1 1 .3 1.5L8 8.4l-1.4.7.3-1.5-1.1-1 1.5-.2z',
  '/booking-requests': 'M3 3.2h10v9.6H3z M5.2 6h5.6 M5.2 8.4h3.8 M10.2 10.8l1.1 1.1 2-2.2',
  '/proposals': 'M4 2.6h5.6l2.4 2.4v8.4H4z M9.4 2.6v2.6H12 M8 7.4v4 M6 9.4h4',
  '/careers': 'M3 4.4h10v8.4H3z M6 4.4V3.2h4v1.2 M3 8h10 M7.2 8v1.2h1.6V8',
  '/spaces': 'M2.4 4.2h4.6v3.2H2.4z M9 4.2h4.6v3.2H9z M2.4 9.4h4.6v3.2H2.4z M9 9.4h4.6v3.2H9z',
  '/payments': 'M2.3 4.6h11.4v6.8H2.3z M2.3 7.1h11.4 M4.3 9.4h2.4',
  '/expenses': 'M4 2.6h8v10.8H4z M6 5.3h4 M6 8h4 M6 10.7h2',
  '/analytics': 'M2.6 13.4V9.2 M6.2 13.4V4.4 M9.8 13.4V7 M13.4 13.4V2.6',
  '/customers':
    'M6.2 7.4a2.1 2.1 0 100-4.2 2.1 2.1 0 000 4.2z M2.6 13c.6-2.1 1.9-3.2 3.6-3.2S9.2 10.9 9.8 13 M10.6 3.6a2 2 0 010 3.9 M11.4 9.9c1.2.4 2 1.4 2.4 3.1',
  '/surveys/business-needs':
    'M4 2.6h8v10.8H4z M6 5.4l.7.7 1.3-1.4 M6 8.4l.7.7 1.3-1.4 M9.4 5.6h1.2 M9.4 8.6h1.2 M6 11.2h4.6',
  '/packages': 'M2.8 8.4l5.6-5.6h4.8v4.8L7.6 13.2z M10.9 5.1h.01',
  '/booking-policy': 'M4 2.6h8v10.8H4z M6 5.4h4 M6 7.8h4 M6 10.2h2.4',
  '/events': 'M8 2.6l1.7 3.5 3.8.5-2.8 2.7.7 3.8L8 11.3l-3.4 1.8.7-3.8L2.5 6.6l3.8-.5z',
  '/events/attendees':
    'M5.6 7a2 2 0 100-4 2 2 0 000 4z M2.4 12.6c.5-2 1.6-3 3.2-3s2.7 1 3.2 3 M10 7.6l1.4 1.4 2.6-2.8',
  '/check-in':
    'M2.6 2.6h4v4h-4z M9.4 2.6h4v4h-4z M2.6 9.4h4v4h-4z M9.4 9.4h1.6v1.6H9.4z M12.6 9.4h.8v.8h-.8z M11.8 12.2h1.6v1.2h-1.6z',
  '/administrators':
    'M8 8.2a2.4 2.4 0 100-4.8 2.4 2.4 0 000 4.8z M3.2 13.6c.7-2.4 2.3-3.6 4.8-3.6s4.1 1.2 4.8 3.6',
  '/roles': 'M8 2.4l4.8 2v3.4c0 2.6-1.9 4.6-4.8 5.8-2.9-1.2-4.8-3.2-4.8-5.8V4.4z',
  '/audit-log': 'M4 2.6h6l2.4 2.4v8.4H4z M9.6 2.6v2.8h2.8 M6 8.4h4 M6 10.8h4',
  '/files': 'M2.4 4.4a1 1 0 011-1h3l1.4 1.6h4.8a1 1 0 011 1v6.6a1 1 0 01-1 1H3.4a1 1 0 01-1-1z',
  '/cafe':
    'M3 5.6h8v4.2a3 3 0 01-3 3H6a3 3 0 01-3-3z M11 6.6h1.2a1.6 1.6 0 010 3.2H11 M5.6 2.6v1.4 M8.4 2.6v1.4',
  '/communications':
    'M2.6 6.4l8.8-3.6v10.4L2.6 9.6z M11.4 5.6c1.2.4 2 1.4 2 2.4s-.8 2-2 2.4 M4.6 9.8l.8 3.4h1.8l-.6-2.8',
  '/notifications':
    'M8 2.6a3.6 3.6 0 013.6 3.6c0 3 1.2 4 1.2 4H3.2s1.2-1 1.2-4A3.6 3.6 0 018 2.6z M6.6 12.2a1.6 1.6 0 002.8 0',
};

/**
 * The sidebar's sections. A section is a title, not a page: it folds and
 * unfolds the pages under it. Its icon stands for it only in the full-width
 * sidebar; the icon-only one shows every page's own icon instead.
 */
const SECTIONS = [
  { key: 'spaces', label: 'Spaces & bookings', icon: ICONS['/spaces'] },
  { key: 'events', label: 'Events', icon: ICONS['/events'] },
  { key: 'people', label: 'People', icon: ICONS['/customers'] },
  { key: 'finance', label: 'Finance', icon: ICONS['/payments'] },
  { key: 'content', label: 'Content', icon: ICONS['/files'] },
  { key: 'communications', label: 'Communications', icon: ICONS['/communications'] },
  { key: 'administration', label: 'Administration', icon: ICONS['/roles'] },
] as const;

type SectionKey = (typeof SECTIONS)[number]['key'];

/**
 * Every page in the sidebar, in sidebar order. `label` is the sidebar's word
 * for it, `title` and `sub` head the page. An entry with no `section` stands
 * on its own above the sections.
 */
const NAV: {
  to: string;
  label: string;
  title: string;
  sub: string;
  permission?: string;
  section?: SectionKey;
}[] = [
  { to: '/', label: 'Dashboard', title: 'Dashboard', sub: 'Tuesday 8 September 2026 · front desk' },

  {
    to: '/spaces',
    label: 'Spaces',
    title: 'Spaces',
    sub: 'Book a desk from the floor plan, or a room by the hour',
    permission: 'facilities.read',
    section: 'spaces',
  },
  {
    to: '/bookings',
    label: 'Bookings',
    title: 'Space booking',
    sub: 'Manual booking across all five spaces',
    permission: 'reservations.read',
    section: 'spaces',
  },
  {
    to: '/bookings/feedback',
    label: 'Booking feedback',
    title: 'Booking feedback',
    sub: 'What clients thought of the spaces they booked, and the forms that ask them',
    permission: 'reservations.read',
    section: 'spaces',
  },
  {
    to: '/booking-requests',
    label: 'Booking requests',
    title: 'Booking requests',
    sub: 'Review desk and meeting-room requests sent from the public booking forms',
    permission: 'reservations.read',
    section: 'spaces',
  },
  {
    to: '/proposals',
    label: 'Proposals',
    title: 'Proposals',
    sub: 'Review and quote spaces configured to use the proposal workflow',
    permission: 'reservations.read',
    section: 'spaces',
  },
  {
    to: '/booking-policy',
    label: 'Booking policy',
    title: 'Booking policy',
    sub: 'What visitors accept before booking, in English and Arabic',
    permission: 'content.read',
    section: 'spaces',
  },

  {
    to: '/events',
    label: 'All events',
    title: 'Events',
    sub: 'The programme, who is coming, and what they thought',
    permission: 'events.read',
    section: 'events',
  },
  {
    to: '/events/attendees',
    label: 'Attendees',
    title: 'Attendees',
    sub: "An event's guest list: who is coming, and who has arrived",
    permission: 'events.read',
    section: 'events',
  },
  {
    to: '/check-in',
    label: 'Check-in',
    title: 'Check-in',
    sub: 'Scan a QR ticket or check a guest in by hand',
    permission: 'events.checkin',
    section: 'events',
  },

  {
    to: '/customers',
    label: 'Customers',
    title: 'Customers',
    sub: 'Search, add and open a customer record',
    permission: 'crm.customers.read',
    section: 'people',
  },
  {
    to: '/careers',
    label: 'Careers',
    title: 'Careers',
    sub: 'Join-the-crew applications from the careers form',
    permission: 'applications.read',
    section: 'people',
  },

  {
    to: '/surveys/business-needs',
    label: 'Business survey',
    title: 'Business needs survey',
    sub: 'Type in the digital needs questionnaires businesses filled in',
    permission: 'surveys.read',
    section: 'people',
  },

  {
    to: '/payments',
    label: 'Payments',
    title: 'Payments',
    sub: 'Ledger, balances and manual entry',
    permission: 'payments.read',
    section: 'finance',
  },
  {
    to: '/expenses',
    label: 'Expenses',
    title: 'Expenses',
    sub: 'Versioned outgoing costs, receipts and supporting documents',
    permission: 'expenses.read',
    section: 'finance',
  },
  {
    to: '/packages',
    label: 'Packages & pricing',
    title: 'Packages & pricing',
    sub: 'The rate card every quote is priced from',
    permission: 'facilities.read',
    section: 'finance',
  },
  {
    to: '/analytics',
    label: 'Analytics',
    title: 'Analytics',
    sub: 'What sells, and what earns',
    permission: 'reports.read',
    section: 'finance',
  },

  {
    to: '/cafe',
    label: 'Café',
    title: 'Café page',
    sub: 'The café copy and menu, drafted and published',
    permission: 'content.read',
    section: 'content',
  },
  {
    to: '/files',
    label: 'Files',
    title: 'Files',
    sub: 'Upload, search and manage stored files',
    permission: 'content.read',
    section: 'content',
  },

  {
    to: '/communications',
    label: 'Announcements',
    title: 'Communications',
    sub: 'Announcements and invitations to customers who agreed to email',
    permission: 'communications.read',
    section: 'communications',
  },
  {
    to: '/notifications',
    label: 'Deliveries',
    title: 'Delivery history',
    sub: 'Whether the mail the system sent arrived',
    permission: 'communications.read',
    section: 'communications',
  },

  {
    to: '/administrators',
    label: 'Administrators',
    title: 'Administrators',
    sub: 'Staff accounts, roles and sessions',
    permission: 'admin.users.read',
    section: 'administration',
  },
  {
    to: '/roles',
    label: 'Roles',
    title: 'Roles & permissions',
    sub: 'What each role is allowed to do',
    permission: 'admin.users.read',
    section: 'administration',
  },
  {
    to: '/audit-log',
    label: 'Audit log',
    title: 'Audit log',
    sub: 'Who did what, and when',
    permission: 'audit.read',
    section: 'administration',
  },
];

const UNKNOWN_ROUTE = { title: 'Not found', sub: 'That admin route does not exist yet' };

function Icon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d={d} />
    </svg>
  );
}

type NavEntry = (typeof NAV)[number];

const SECTIONS_KEY = 'mds.admin.nav-sections';

/** Sections folded or unfolded by hand, kept across visits. */
function readSections(): Partial<Record<SectionKey, boolean>> {
  try {
    const raw = localStorage.getItem(SECTIONS_KEY);
    return raw ? (JSON.parse(raw) as Partial<Record<SectionKey, boolean>>) : {};
  } catch {
    return {};
  }
}

const focusOnPanel =
  'focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-focus-on-panel';

/**
 * One page link. `active` comes from the header's own route match, so a
 * detail route such as /events/:id lights the page it belongs to. `iconOnly`
 * is the narrow sidebar; `nested` is a page under an open section title,
 * drawn without its icon so the title's icon is the one that reads.
 */
function NavItem({
  item: n,
  active,
  iconOnly,
  nested,
}: {
  item: NavEntry;
  active: boolean;
  iconOnly: boolean;
  nested?: boolean;
}) {
  return (
    <Link
      to={n.to}
      title={n.label}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        if (!confirmLeave()) e.preventDefault();
      }}
      className={cn(
        'flex min-h-(--target-control) items-center gap-3 rounded-xl border px-3 text-base transition-colors duration-(--duration-fast)',
        focusOnPanel,
        iconOnly ? 'justify-center px-0 py-3' : nested ? 'py-2' : 'py-3',
        active
          ? 'border-brand/50 bg-brand/15 font-semibold text-brand'
          : 'border-transparent font-medium text-on-panel/90 hover:bg-on-panel/10 hover:text-on-panel',
      )}
    >
      {!nested && <Icon d={ICONS[n.to]} />}
      {!iconOnly && <span className="truncate">{n.label}</span>}
    </Link>
  );
}

/**
 * The admin shell: a collapsible sidebar, a header that titles the current
 * route from NAV, and an <Outlet> for the page. Below `lg` the sidebar leaves
 * the row and becomes a drawer, opened from the header's menu button.
 */
export default function AdminLayout() {
  const wide = useBreakpoint('lg');
  const [narrowed, setCollapsed] = useState(false);
  // The drawer is always full width; only the desktop sidebar folds to icons.
  const collapsed = wide && narrowed;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawer = !wide;
  const [folds, setFolds] = useState(readSections);
  const navRef = useRef<HTMLElement>(null);
  // Whether the link list runs past its top or bottom edge, to draw a fade there.
  const [edges, setEdges] = useState({ above: false, below: false });
  const measure = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    setEdges({
      above: nav.scrollTop > 0,
      // A pixel of slack: zoomed screens land scrollTop on a fraction.
      below: nav.scrollTop + nav.clientHeight < nav.scrollHeight - 1,
    });
  }, []);
  const { can, session, signOut } = useSession();
  const [leaving, setLeaving] = useState(false);

  // Disabled while it runs: a second click during the round trip would fire a
  // logout against a session that is already gone.
  const leave = async () => {
    setLeaving(true);
    try {
      await signOut();
    } finally {
      setLeaving(false);
    }
  };
  const { pathname } = useLocation();
  // A detail route such as /events/:id keeps its section heading,
  // matching on the longest nav prefix rather than the exact path.
  const current =
    NAV.find((n) => n.to === pathname) ??
    NAV.filter((n) => n.to !== '/' && pathname.startsWith(`${n.to}/`)).sort(
      (a, b) => b.to.length - a.to.length,
    )[0] ??
    UNKNOWN_ROUTE;

  // An entry with a permission is only offered to an administrator who holds
  // it — the route is guarded too, this just avoids dead links. A section with
  // nothing left in it is not drawn at all.
  const visible = NAV.filter((n) => !n.permission || can(n.permission));
  const loose = visible.filter((n) => !n.section);
  const sections = SECTIONS.map((section) => ({
    ...section,
    pages: visible.filter((n) => n.section === section.key),
  })).filter((section) => section.pages.length > 0);

  // A section starts open when the page you are on is in it. Once it is
  // folded or unfolded by hand, that choice holds, here and across visits.
  const isOpen = (key: SectionKey, pages: NavEntry[]) =>
    folds[key] ?? pages.includes(current as NavEntry);
  const toggle = (key: SectionKey, open: boolean) => {
    const next = { ...folds, [key]: open };
    setFolds(next);
    try {
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(next));
    } catch {
      // Storage blocked: the choice still holds until the page reloads.
    }
  };

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    // The list's own height changes as sections open, not just the box's.
    for (const child of nav.children) observer.observe(child);
    return () => observer.disconnect();
  }, [measure, collapsed]);

  // Any navigation closes the drawer, and so does widening past `lg`.
  useEffect(() => setDrawerOpen(false), [pathname, wide]);

  // While the drawer is open: Escape closes it, and the page behind stays put.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawerOpen(false);
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    navRef.current?.querySelector<HTMLElement>('[aria-current="page"], a, button')?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [drawerOpen]);

  // On arrival and on every route change, the current page's link is scrolled
  // into the list, so a page far down a short sidebar is not left offscreen.
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }, [pathname, collapsed]);

  return (
    <div className="flex min-h-svh bg-page text-ink">
      {drawer && (
        <div
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
          className={cn(
            'fixed inset-0 z-(--z-overlay) bg-black/60 transition-opacity duration-(--duration-base) ease-standard',
            drawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        />
      )}
      <aside
        id="admin-sidebar"
        data-ground="panel"
        role={drawer ? 'dialog' : undefined}
        aria-modal={drawer ? true : undefined}
        aria-label={drawer ? 'Menu' : undefined}
        inert={drawer && !drawerOpen}
        className={cn(
          'top-0 flex h-svh shrink-0 [scrollbar-width:none] flex-col gap-6 overflow-y-auto bg-panel py-6 transition-all duration-(--duration-base) ease-standard',
          drawer
            ? cn(
                'fixed left-0 z-(--z-modal) w-[min(284px,85vw)] px-4 shadow-lift',
                drawerOpen ? 'translate-x-0' : '-translate-x-full',
              )
            : cn('sticky', collapsed ? 'w-[88px] px-3' : 'w-[284px] px-4'),
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 px-1">
          {/* As tall as the row allows before it crowds the collapse button. */}
          {!collapsed && (
            <img src="/images/mds-logo.png" alt="MDS" className="h-[72px] w-auto object-contain" />
          )}
          <button
            onClick={() => (drawer ? setDrawerOpen(false) : setCollapsed((v) => !v))}
            aria-label={drawer ? 'Close menu' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex h-(--target-min) w-11 items-center justify-center rounded-lg border border-on-panel/30 text-on-panel/85 transition-colors hover:border-brand hover:text-brand focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-focus-on-panel"
          >
            {drawer ? (
              <XIcon className="size-5" aria-hidden="true" />
            ) : (
              <svg
                viewBox="0 0 16 16"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d={collapsed ? 'M6 4l4 4-4 4' : 'M10 4L6 8l4 4'} />
              </svg>
            )}
          </button>
        </div>

        {/* The list scrolls on its own between the logo and the account card. Below
            min-h-40 it stops shrinking and the whole sidebar scrolls instead, so a
            very short or zoomed-in screen never squeezes it to nothing. */}
        <div className="relative flex min-h-40 flex-1 flex-col">
          {/* Fades at an edge that has more links past it. */}
          <div
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-linear-to-b from-panel to-transparent transition-opacity duration-(--duration-fast)',
              edges.above ? 'opacity-100' : 'opacity-0',
            )}
          />
          <div
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-linear-to-t from-panel to-transparent transition-opacity duration-(--duration-fast)',
              edges.below ? 'opacity-100' : 'opacity-0',
            )}
          />
          <nav
            ref={navRef}
            onScroll={measure}
            className="-mr-1 flex min-h-0 flex-1 [scrollbar-width:thin] [scrollbar-color:rgb(255_255_255/0.25)_transparent] flex-col gap-1 overflow-y-auto overscroll-contain pr-1"
          >
            {loose.map((n) => (
              <NavItem key={n.to} item={n} active={n === current} iconOnly={collapsed} />
            ))}

            {sections.map((section) => {
              // Narrow: no titles to fold, just each page's icon, a rule between sections.
              if (collapsed) {
                return (
                  <div
                    key={section.key}
                    role="group"
                    aria-label={section.label}
                    className="mt-2 flex flex-col gap-1 border-t border-on-panel/15 pt-2"
                  >
                    {section.pages.map((n) => (
                      <NavItem key={n.to} item={n} active={n === current} iconOnly />
                    ))}
                  </div>
                );
              }

              const open = isOpen(section.key, section.pages);
              const listId = `nav-section-${section.key}`;
              // Folded over the page you are on: the title says where you are.
              const holdsCurrent = section.pages.includes(current as NavEntry);
              return (
                <div key={section.key} className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => toggle(section.key, !open)}
                    aria-expanded={open}
                    aria-controls={listId}
                    className={cn(
                      'flex min-h-(--target-control) w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-base font-semibold transition-colors duration-(--duration-fast) hover:bg-on-panel/10',
                      focusOnPanel,
                      holdsCurrent && !open ? 'text-brand' : 'text-on-panel',
                    )}
                  >
                    <Icon d={section.icon} />
                    <span className="min-w-0 flex-1 truncate">{section.label}</span>
                    <ChevronDownIcon
                      aria-hidden="true"
                      className={cn(
                        'size-5 shrink-0 text-on-panel/60 transition-transform duration-(--duration-fast) ease-standard',
                        !open && '-rotate-90',
                      )}
                    />
                  </button>
                  {/* Opens from 0fr to 1fr; inert keeps folded links out of the tab order. */}
                  <div
                    id={listId}
                    inert={!open}
                    onTransitionEnd={(e) => {
                      if (!open || e.target !== e.currentTarget) return;
                      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
                      e.currentTarget.parentElement?.scrollIntoView({
                        block: 'nearest',
                        behavior: reduce ? 'auto' : 'smooth',
                      });
                    }}
                    className={cn(
                      'grid transition-[grid-template-rows] duration-(--duration-base) ease-standard',
                      open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                  >
                    <div className="min-h-0 overflow-hidden">
                      {/* The rail sits under the title's icon, so the pages read as its own. */}
                      <div className="mt-1 mb-2 ml-[22px] flex flex-col gap-1 border-l border-on-panel/15 pl-3">
                        {section.pages.map((n) => (
                          <NavItem
                            key={n.to}
                            item={n}
                            active={n === current}
                            iconOnly={false}
                            nested
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>
        </div>

        {/* Who is signed in, and the way out, pinned to the foot of the sidebar. */}
        <div className="shrink-0">
          {session?.user ? (
            <div
              className={cn(
                'flex items-center gap-2.5 rounded-xl border border-on-panel/20 p-3',
                collapsed && 'flex-col gap-2 p-2',
              )}
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/20 font-mono text-md font-semibold text-brand"
                title={session.user.email}
              >
                {initials(session.user.firstName, session.user.lastName, session.user.email)}
              </span>
              {!collapsed && (
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-semibold text-on-panel">
                    {displayName(session.user.firstName, session.user.lastName, session.user.email)}
                  </span>
                  <span className="truncate font-mono text-2xs tracking-normal text-on-panel/60 uppercase">
                    {session.roles[0]?.name ?? 'Administrator'}
                  </span>
                </span>
              )}
              <button
                type="button"
                onClick={() => void leave()}
                disabled={leaving}
                title="Sign out"
                aria-label="Sign out"
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-on-panel/25 text-on-panel/80 transition-colors hover:border-brand hover:text-brand focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-focus-on-panel disabled:opacity-50',
                  !collapsed && 'ml-auto',
                )}
              >
                <LogOutIcon className="size-5" strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>
          ) : (
            !collapsed && (
              <div className="rounded-xl border border-on-panel/20 p-3 font-mono text-sm tracking-wide text-on-panel/60 uppercase">
                Not signed in
              </div>
            )
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-(--z-sticky) flex items-center justify-between gap-3 border-b-2 border-hairline bg-page/95 px-gutter py-3 backdrop-blur sm:gap-4 sm:py-4 lg:px-9">
          {drawer && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls="admin-sidebar"
              className="-ml-2 flex size-(--target-min) shrink-0 items-center justify-center rounded-lg text-ink transition-colors hover:bg-ink/5 focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
            >
              <MenuIcon className="size-6" aria-hidden="true" />
            </button>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <h1 className="m-0 truncate text-xl font-semibold tracking-tighter sm:text-2xl">
              {current.title}
            </h1>
            <span className="line-clamp-2 font-mono text-xs tracking-wide text-ink-secondary uppercase sm:text-sm">
              {current.sub}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden font-mono text-sm tracking-wide text-ink-secondary uppercase sm:block">
              Front desk
            </span>
            <UserMenu />
          </div>
        </header>

        <main className="flex-1 px-gutter py-block">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
